import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  fetchOaPoAudit,
  type OaAuditFinding,
  type OaFindingType,
  type OaPoAuditData,
} from '@/api/controle';

/**
 * Controle — onderaannemer rolling-PO settlement audit.
 *
 * Recomputes every open [LOPEND] PO line against the m² each installer ACTUALLY placed in
 * each fase, and shows where the billed rate disagrees. Read-only: this panel proposes
 * nothing and writes nothing — the reviewer corrects the PO in Odoo (or the PO-edit screen).
 *
 * Findings are review items, not verdicts. A manually overridden tier or a hand-typed €/m²
 * rate is not persisted anywhere, so it shows up here as a mismatch — which is exactly why
 * every row shows its derivation (basis m², expected vs billed) instead of just a red cross.
 */

const TYPE_META: Record<OaFindingType, { label: string; hint: string }> = {
  tier_mismatch: {
    label: 'Verkeerd tarief',
    hint: 'Het tarief hoort bij de m² die deze onderaannemer zélf in deze fase plaatste — niet bij het totaal van de gedeelde materiaallijst. Let op: een handmatig ingestelde staffel of een zelf getypt €/m²-tarief wordt nergens bewaard, dus die verschijnt hier óók als afwijking. Vandaar dat elke regel zijn berekening toont.',
  },
  installateur_mismatch: {
    label: 'Andere installateur',
    hint: 'De collector is geplaatst door iemand anders dan de leverancier van deze PO — deze onderaannemer wordt betaald voor werk van een ander.',
  },
  collector_geleverd: {
    label: 'Collector al geplaatst',
    hint: 'De fase staat op "collectoren geleverd" — deze collector hoort op minprijs, niet op vol tarief.',
  },
  btw_mismatch: {
    label: 'Btw',
    hint: 'Onderaannemerslijnen horen op 0% verlegd (S.Cocont).',
  },
  missing_price: {
    label: 'Prijs te bepalen',
    hint: 'Plaatsing staat bewust op €0 tot er een tarief is afgesproken. Vul in vóór de definitieve PO.',
  },
  no_supplierinfo: {
    label: 'Geen leveranciersprijs',
    hint: 'Zonder supplierinfo voor dit product bij deze onderaannemer valt het tarief niet te verifiëren.',
  },
  basis_unknown: {
    label: 'Basis onbekend',
    hint: 'Zonder materiaallijst is de fase — en dus de m²-basis — niet te bepalen. Koppel de materiaallijst.',
  },
  multi_installateur: {
    label: 'Meerdere installateurs',
    hint: 'De m² van deze collector zijn niet automatisch aan één onderaannemer toe te wijzen.',
  },
};

const SEVERITY_STYLE = {
  error: { dot: 'bg-red-500', row: 'border-l-red-500' },
  warning: { dot: 'bg-amber-400', row: 'border-l-amber-400' },
  info: { dot: 'bg-slate-300', row: 'border-l-slate-300' },
} as const;

const eur = (n: number): string =>
  `${n > 0 ? '+' : n < 0 ? '−' : ''}€${Math.abs(n).toFixed(2)}`;

function FindingRow({ f }: { f: OaAuditFinding }) {
  const style = SEVERITY_STYLE[f.severity];
  const where = [f.projectName, f.faseName, f.collectorName].filter(Boolean).join(' · ');
  const d = f.detail;

  return (
    <div className={`border-l-4 ${style.row} bg-white rounded-r-lg px-4 py-3`}>
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`w-2 h-2 rounded-full shrink-0 ${style.dot}`} />
            <span className="text-sm font-semibold text-td-navy">{TYPE_META[f.type].label}</span>
            {f.readiness === 'voorlopig' && (
              <span
                className="px-1.5 py-0.5 rounded bg-slate-100 text-slate-500 text-[10px] font-semibold"
                title="Niet elke collector in deze fase is goedgekeurd — de m²-basis kan nog wijzigen. Kijken, nog niet corrigeren."
              >
                voorlopig
              </span>
            )}
          </div>
          <p className="text-sm text-slate-700 mt-1">{f.message}</p>
          {/* Link to the project when we know it (e.g. basis_unknown, where the reviewer must go
              open the werf and link its materiaallijst). Fall back to the raw line description: a
              btw finding on a minprijs line has no collector, so the reviewer at least sees which
              line is wrong. */}
          {f.projectId !== null ? (
            <Link
              to={`/project/${f.projectId}`}
              className="text-xs text-td-blue hover:underline mt-0.5 inline-block truncate max-w-full"
            >
              {where || `Project #${f.projectId}`} →
            </Link>
          ) : (
            <p className="text-xs text-slate-400 mt-0.5 truncate">{where || f.lineName}</p>
          )}

          {/* Show the work: the reviewer must be able to disagree with us. */}
          {f.type === 'tier_mismatch' && d.basisM2 !== undefined && (
            <p className="text-xs text-slate-500 mt-1.5 font-mono">
              {d.m2} m² × (€{d.billedRate?.toFixed(2)} → €{d.expectedRate?.toFixed(2)})
              <span className="text-slate-400"> · basis {d.basisM2} m² in deze fase</span>
            </p>
          )}
        </div>

        {f.deltaEur !== null && f.deltaEur !== 0 && (
          <span
            className={`shrink-0 text-sm font-bold tabular-nums ${
              f.deltaEur > 0 ? 'text-red-600' : 'text-amber-600'
            }`}
            title={f.deltaEur > 0 ? 'Te veel aangerekend' : 'Te weinig uitbetaald aan de OA'}
          >
            {eur(f.deltaEur)}
          </span>
        )}
      </div>
    </div>
  );
}

export function OaPoAudit() {
  const [data, setData] = useState<OaPoAuditData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [typeFilter, setTypeFilter] = useState<OaFindingType | 'all'>('all');
  const [definitiefOnly, setDefinitiefOnly] = useState(false);

  const load = () => {
    setLoading(true);
    setError(null);
    fetchOaPoAudit()
      .then(setData)
      .catch((e: unknown) => setError(e instanceof Error ? e.message : 'Audit mislukt'))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const visible = useMemo(() => {
    if (!data) return [];
    return data.findings.filter(
      (f) =>
        (typeFilter === 'all' || f.type === typeFilter) &&
        (!definitiefOnly || f.readiness === 'definitief'),
    );
  }, [data, typeFilter, definitiefOnly]);

  const byPo = useMemo(() => {
    const map = new Map<number, OaAuditFinding[]>();
    for (const f of visible) {
      const list = map.get(f.poId) ?? [];
      list.push(f);
      map.set(f.poId, list);
    }
    return map;
  }, [visible]);

  if (loading) {
    return <div className="px-8 py-10 text-sm text-slate-400">Audit draait — even geduld…</div>;
  }
  if (error) {
    return (
      <div className="px-8 py-10">
        <p className="text-sm text-red-600">{error}</p>
        <button
          type="button"
          onClick={load}
          className="mt-3 px-3 py-1.5 rounded-lg bg-td-blue text-white text-sm font-semibold"
        >
          Opnieuw proberen
        </button>
      </div>
    );
  }
  if (!data) return null;

  const activeTypes = (Object.keys(data.counts) as OaFindingType[]).filter(
    (t) => data.counts[t] > 0,
  );

  return (
    <div className="px-8 py-6 space-y-5">
      {/* Headline — over- and underpayment shown SEPARATELY. Netting them into one figure
          would let €500 te veel and €500 te weinig cancel into a reassuring green €0. */}
      <div className="flex items-start justify-between gap-6 flex-wrap">
        <div>
          <p className="text-sm text-slate-500">
            {data.pos.length} lopende PO's · {data.findings.length} bevinding(en)
          </p>
          <p className="text-xs text-slate-400 mt-1 max-w-2xl">
            Bij een gedeelde materiaallijst kan een onderaannemer op het verkeerde staffeltarief
            staan. Deze audit rekent elke lijn na op de m² die de onderaannemer zélf in die fase
            plaatste. Controleer vóór je de definitieve PO verstuurt.
          </p>
        </div>
        <div className="flex gap-6 text-right">
          <div>
            <p className="text-xs text-slate-400">Te veel aangerekend</p>
            <p
              className={`text-2xl font-bold tabular-nums ${
                data.totalOverEur > 0 ? 'text-red-600' : 'text-td-green'
              }`}
            >
              €{data.totalOverEur.toFixed(2)}
            </p>
          </div>
          <div>
            <p className="text-xs text-slate-400">Te weinig uitbetaald</p>
            <p
              className={`text-2xl font-bold tabular-nums ${
                data.totalUnderEur > 0 ? 'text-amber-600' : 'text-td-green'
              }`}
            >
              €{data.totalUnderEur.toFixed(2)}
            </p>
          </div>
        </div>
      </div>
      <p className="text-[11px] text-slate-400 -mt-3 text-right">
        alleen definitieve fases — voorlopige tellen niet mee
      </p>

      {data.btwCheckSkipped && (
        <div className="rounded-lg bg-amber-50 border border-amber-200 px-4 py-2 text-xs text-amber-800">
          De btw-code "0% verlegd (S.Cocont)" is niet gevonden in Odoo — de btw-check is
          overgeslagen.
        </div>
      )}

      {/* Filters */}
      <div className="flex items-center gap-2 flex-wrap">
        <button
          type="button"
          onClick={() => setTypeFilter('all')}
          className={`px-3 py-1 rounded-full text-xs font-semibold border ${
            typeFilter === 'all'
              ? 'bg-td-navy text-white border-td-navy'
              : 'bg-white text-slate-500 border-slate-200 hover:text-slate-700'
          }`}
        >
          Alles ({data.findings.length})
        </button>
        {activeTypes.map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => setTypeFilter(t)}
            title={TYPE_META[t].hint}
            className={`px-3 py-1 rounded-full text-xs font-semibold border ${
              typeFilter === t
                ? 'bg-td-navy text-white border-td-navy'
                : 'bg-white text-slate-500 border-slate-200 hover:text-slate-700'
            }`}
          >
            {TYPE_META[t].label} ({data.counts[t]})
          </button>
        ))}
        <label className="ml-auto flex items-center gap-2 text-xs text-slate-500 cursor-pointer">
          <input
            type="checkbox"
            checked={definitiefOnly}
            onChange={(e) => setDefinitiefOnly(e.target.checked)}
            className="rounded border-slate-300"
          />
          Alleen definitief
        </label>
      </div>

      {visible.length === 0 ? (
        // A filter that hides every finding must NOT render as an all-clear — that is the same
        // false-reassurance bug as netting the totals, one layer up.
        data.findings.length === 0 ? (
          <div className="rounded-xl bg-white border border-slate-200 px-6 py-10 text-center">
            <p className="text-sm font-semibold text-td-green">Geen afwijkingen</p>
            <p className="text-xs text-slate-400 mt-1">
              Elke lopende PO-lijn komt overeen met het tarief dat de onderaannemer verdiende.
            </p>
          </div>
        ) : (
          <div className="rounded-xl bg-white border border-slate-200 px-6 py-10 text-center">
            <p className="text-sm font-semibold text-slate-600">
              Geen bevindingen in deze selectie
            </p>
            <p className="text-xs text-slate-400 mt-1">
              Er zijn wél {data.findings.length} bevinding(en) — pas je filter aan om ze te zien.
            </p>
          </div>
        )
      ) : (
        data.pos
          .filter((po) => (byPo.get(po.poId)?.length ?? 0) > 0)
          .map((po) => (
            <section key={po.poId} className="space-y-2">
              <header className="flex items-baseline justify-between gap-4 flex-wrap">
                <h3 className="text-sm font-bold text-td-navy">
                  {po.poName}
                  <span className="ml-2 font-normal text-slate-500">{po.vendorName}</span>
                </h3>
                <p className="text-xs text-slate-400 tabular-nums">
                  PO €{po.amountUntaxed.toFixed(2)}
                  {po.deltaEur !== 0 && (
                    <span className={po.deltaEur > 0 ? ' text-red-600 font-semibold' : ' text-amber-600 font-semibold'}>
                      {' '}
                      · saldo {eur(po.deltaEur)}
                    </span>
                  )}
                </p>
              </header>
              <div className="space-y-1.5">
                {byPo.get(po.poId)!.map((f, i) => (
                  <FindingRow key={`${f.lineId ?? 'x'}-${f.type}-${i}`} f={f} />
                ))}
              </div>
            </section>
          ))
      )}
    </div>
  );
}
